/**
 * Quest Studio ← real server payloads, and the quest draft → the real write body (#577 / R6.2).
 *
 * | screen part | endpoint | shape |
 * | --- | --- | --- |
 * | packs | `GET /api/quest-packs` | `{ success, selectionMode, asOfDate, timezone, packs }` |
 * | kids | `GET /api/admin/users?includeSelf=true` via `familyMapping.toMembers` | mapped members |
 * | board | `GET /api/admin/chores` | `{ success, chores, meta }` |
 * | save | `POST /api/admin/chores` | `createChoreSchema` |
 *
 * The WRITE half is the part that has to be exact. `QuestStudio.handleSave` builds
 * `{name, icon, reward, xp, assignedKids, rotate, frequency, days, requiresApproval}` and then
 * checks that the host's receipt echoes every one of those fields. A mapping that quietly drops
 * `days` would still save a quest, and the parent's choice of days would disappear — which is the
 * exact defect `migrations/…_r62-quest-schedule-days.sql` was written to end.
 */

import { formatMoney, formatPlainAmount, knownCurrency } from './heldMoney.js';

/** The screen's day keys, in ISO order. Monday = 1 … Sunday = 7, matching `EXTRACT(ISODOW …)`. */
export const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const isObject = (v) => v !== null && typeof v === 'object';
/** Monday = 1 … Sunday = 7, read in LOCAL time — the clock `toBoard` places cards by. */
export const isoWeekday = (date) => (date.getDay() === 0 ? 7 : date.getDay());

/**
 * Screen day keys → ISO weekday numbers, sorted and de-duplicated.
 *
 * **`null` is a destination, not a fallback.** At the column `null` means "this quest may fall on
 * any day", so arriving there by accident silently BROADENS a Monday-and-Thursday quest to every
 * day of the week. Only one input may reach it: an explicit empty array, which is what the picker
 * produces when a parent deliberately clears every day.
 *
 * Everything else that cannot be understood throws. An earlier version dropped unrecognised keys
 * and mapped an all-unknown selection to `null`, so a renamed or stale picker value would have
 * widened the schedule instead of refusing the save. A cross-model reviewer found that.
 *
 * @throws when `dayKeys` is absent, is not an array, or holds a key that is not a weekday
 */
export function toScheduleDays(dayKeys) {
  if (!Array.isArray(dayKeys)) {
    throw Object.assign(
      new Error('The days for this quest are missing or malformed, so it was not saved'),
      { statusCode: 400 },
    );
  }
  if (dayKeys.length === 0) return null;

  const iso = [];
  for (const key of dayKeys) {
    const i = DAY_KEYS.indexOf(String(key).toLowerCase());
    if (i < 0) {
      throw Object.assign(
        new Error(`"${key}" is not a day of the week, so this quest was not saved`),
        { statusCode: 400 },
      );
    }
    iso.push(i + 1);
  }
  return [...new Set(iso)].sort((a, b) => a - b);
}

/** ISO weekday numbers → the screen's day keys, for re-opening a saved quest. */
export function fromScheduleDays(iso) {
  if (!Array.isArray(iso)) return [];
  return iso
    .filter((n) => Number.isInteger(n) && n >= 1 && n <= 7)
    .sort((a, b) => a - b)
    .map((n) => DAY_KEYS[n - 1]);
}

/**
 * When a newly created quest first falls due.
 *
 * Always STRICTLY after today, even when today is an allowed day. A quest created on Monday
 * morning that is already due on Monday morning would be late before the parent closed the
 * screen. Starting at tomorrow and walking forward is also what makes "the soonest allowed day"
 * fall out without a special case.
 */
export function nextExecutionFrom(now, scheduleDays) {
  const due = new Date(now);
  due.setDate(due.getDate() + 1);
  due.setHours(0, 0, 1, 0);
  if (Array.isArray(scheduleDays) && scheduleDays.length > 0) {
    const allowed = new Set(scheduleDays);
    // Bounded at seven: a non-empty subset of seven weekdays always contains one within a week.
    for (let step = 0; step < 7 && !allowed.has(isoWeekday(due)); step += 1) {
      due.setDate(due.getDate() + 1);
    }
  }
  return due;
}

/**
 * The quest draft the screen hands to `onSave` → the `POST /api/admin/chores` body.
 *
 * `assignedKids` is filtered against the real member list. An id the family does not own would be
 * refused by `validateAssignmentConfig` anyway, and refusing it here gives the parent a screen
 * that behaves rather than a 400 they cannot act on.
 *
 * `xp` is omitted when the screen carried no number, so `chores.xp` keeps its own default instead
 * of this layer writing the same value by a second route.
 */
/**
 * The assignment, derived ONCE for all three bodies that need it.
 *
 * `rotate` off still means "somebody does it" when people are assigned, so it becomes the
 * server's other real choice rather than no assignment at all. This lived as the same ternary in
 * `toCreateChoreBody` and `toUpdateChoreBody`; the rotation preview would have been a third copy,
 * and a preview that read `rotate` differently from the save would draw a week the save then
 * contradicts.
 */
function assignmentFrom(draft, kids) {
  const d = isObject(draft) ? draft : {};
  const ownIds = new Set((Array.isArray(kids) ? kids : []).map((k) => k && k.id));
  const assigned = (Array.isArray(d.assignedKids) ? d.assignedKids : []).filter((id) => ownIds.has(id));
  return {
    assignmentType: assigned.length === 0 ? 'no-assignment' : d.rotate ? 'in-alphabetical-order' : 'random',
    assignmentConfig: assigned,
  };
}

/**
 * The body `POST /api/admin/chores/rotation-preview` reads (#577 L19-L22).
 *
 * It asks the SERVER who gets the quest on each of the next seven days, because rotation advances
 * one step per COMPLETION and only the server's own `_calculateNextAssignee` knows that rule.
 *
 * It differs from the write bodies in one way, and the difference is deliberate: it never throws.
 * `toScheduleDays` refuses a non-array AND an unrecognized key rather than silently broadening a
 * quest, which is right for a save. This runs while a parent is still choosing, so an unfinished
 * draft answers `null` days and lets the server word the refusal the screen prints.
 *
 * Both refusal shapes are caught, not just the non-array one (WF5 finding): the guard used to test
 * `Array.isArray` only, so an array holding a key `toScheduleDays` does not recognize still threw
 * — out of a function whose own comment promised it would not, and into a render.
 */
export function toRotationPreviewBody(draft, kids) {
  const d = isObject(draft) ? draft : {};
  let scheduleDays = null;
  try {
    if (Array.isArray(d.days)) scheduleDays = toScheduleDays(d.days);
  } catch {
    scheduleDays = null;
  }
  return {
    scheduleDays,
    ...assignmentFrom(d, kids),
    currentAssigneeId: Number.isInteger(d.currentAssigneeId) ? d.currentAssigneeId : null,
  };
}

export function toCreateChoreBody(draft, kids, now = new Date()) {
  const d = isObject(draft) ? draft : {};
  const name = typeof d.name === 'string' ? d.name.trim() : '';
  if (!name) {
    throw Object.assign(new Error('A quest needs a name before it can be saved'), { statusCode: 400 });
  }

  const scheduleDays = toScheduleDays(d.days);

  const body = {
    name,
    periodType: ['daily', 'weekly', 'monthly'].includes(d.frequency) ? d.frequency : 'weekly',
    reward: typeof d.reward === 'number' && d.reward >= 0 ? d.reward : 0,
    ...assignmentFrom(d, kids),
    scheduleDays,
    nextExecutionTime: nextExecutionFrom(now, scheduleDays).toISOString(),
  };
  if (typeof d.xp === 'number') body.xp = d.xp;
  if (typeof d.requiresApproval === 'boolean') body.requiresApproval = d.requiresApproval;
  // `chores.icon_file` stores a plain FILENAME such as "vacuum.png", read from the family's own
  // icons directory by `getAvailableIcons` (`chore-admin.service.js:1011`). It is NOT a built-in
  // glyph id. The container therefore feeds the picker from `GET /api/admin/chores/icons`, so
  // whatever arrives here is already a real filename. An absent or blank choice is omitted rather
  // than written as an empty filename.
  const icon = typeof d.icon === 'string' ? d.icon.trim() : '';
  if (icon) body.iconFile = icon;
  return body;
}

/**
 * `GET /api/quest-packs` → the `packs` prop.
 *
 * Only packs the family can actually add survive. A retired or empty pack still comes back from
 * the endpoint, carrying `available: false` and a reason, and a card a parent can press that
 * cannot work is worse than no card.
 */
export function toQuestPacks(payload) {
  const packs = isObject(payload) && Array.isArray(payload.packs) ? payload.packs : [];
  return packs
    .filter((p) => isObject(p) && p.available !== false)
    .map((p) => ({
      id: p.id,
      title: p.title,
      questCount: typeof p.questCount === 'number' ? p.questCount : undefined,
      // The server's own caption. `undefined` when it has none, never a made-up price range.
      valueLabel: typeof p.age === 'string' && p.age.trim() ? p.age : undefined,
      iconCount: Array.isArray(p.icons) ? p.icons.length : undefined,
    }));
}

/**
 * `GET /api/quest-packs/:key/templates` answers → the `packDetails` prop (#577 L16).
 *
 * `QuestStudio.jsx` takes a map of packId → `{ title, countLabel, note, templates[] }`, and opens
 * a pack ONLY when that map already holds its id (`QuestStudio.jsx:1186`). So a pack with no
 * answer is simply absent from this map, and its card stays unopenable rather than opening onto
 * an empty panel. That is the same rule the rest of this file follows: no invented row.
 *
 * `value` is built from the server's `suggestedReward`, which crosses as a STRING because the
 * column is `numeric(10,2)`. The validated amount is formatted for the label and carried as a
 * number for quest creation; both currency and plain labels use locale-aware formatting.
 *
 * #580 sweep: the label is in the FAMILY's currency. It used to be a hard-coded `$`. With no known
 * currency it carries the number alone, never an invented unit.
 *
 * @param {Array<{key: string, payload: object}>} answers one entry per pack that answered
 * @param {string|null} [currency] the family's currency code (`GET /api/tenants/current`)
 */
export function toPackDetails(answers, currency) {
  const code = knownCurrency(currency);
  const list = Array.isArray(answers) ? answers.filter(isObject) : [];
  const out = {};

  for (const entry of list) {
    const payload = entry.payload;
    if (!isObject(payload) || !isObject(payload.pack)) continue;

    const templates = (Array.isArray(payload.templates) ? payload.templates : [])
      .filter((t) => isObject(t) && t.id !== undefined && typeof t.name === 'string' && t.name.trim() !== '')
      // A template whose suggested reward is not exact money is DROPPED, not shown without one.
      // `chore_templates.suggested_reward` is `numeric(10,2) NOT NULL`, so anything else means the
      // server broke its own contract. Showing the row anyway used to hand `buildAddTemplate` a
      // reward of `undefined`, which its own `: 0` fallback then turned into a real zero-value
      // quest — this file refusing to guess money, and the create path guessing it one function
      // later. A cross-model reviewer found exactly that. Dropping the row is what the rest of
      // this file already does with anything it cannot state truthfully.
      .filter((t) => typeof t.suggestedReward === 'string' && /^\d+\.\d{2}$/.test(t.suggestedReward))
      .map((t) => {
        // The string is what the database said. It is parsed ONCE, strictly, and only to seed
        // the editable reward on the quest this template creates — the LABEL keeps the string.
        // Safe by the filter above: the string already matched exact money.
        const exact = Number(t.suggestedReward);
        return {
          id: t.id,
          name: t.name,
          value: `${code ? formatMoney(t.suggestedReward, code) : formatPlainAmount(t.suggestedReward)} suggested`,
          reward: exact,
          // `icon` is `chore_templates.icon_name`, which is a CATALOG glyph name. It is NOT a
          // filename from this family's own icons directory, and `chores.icon_file` holds a
          // filename. Carrying it through to a create would write a path to a file that does not
          // exist, which is the trap `toCreateChoreBody` already names. So it is kept for DISPLAY
          // in the pack panel and deliberately dropped on the way into a quest.
          icon: typeof t.icon === 'string' && t.icon.trim() ? t.icon : undefined,
        };
      });

    // An answer with no usable template is NOT recorded. Recording it would let the card open
    // onto a panel with nothing in it, which reads as "this pack is empty" rather than "this
    // did not load".
    if (templates.length === 0) continue;

    const id = payload.pack.id;
    out[id] = {
      title: payload.pack.title,
      countLabel: templates.length === 1 ? '1 quest' : `${templates.length} quests`,
      note: 'Suggested values — each quest is editable after you add it.',
      templates,
    };
  }

  return out;
}

/** The pack ids this family already added, so the screen shows them as added. */
export function addedPackIds(payload) {
  const packs = isObject(payload) && Array.isArray(payload.packs) ? payload.packs : [];
  return packs.filter((p) => isObject(p) && p.added === true).map((p) => p.id);
}

/**
 * Mapped members → the `kids` prop.
 *
 * `age` is deliberately absent. The users endpoint returns `birth_date`, and `familyMapping`
 * does not carry it, so an age here would be computed from nothing.
 */
export function toKids(members) {
  const list = Array.isArray(members) ? members.filter(isObject) : [];
  // `avatar` is the field and value the Family screen shows (`familyMapping.js`, `avatarUrl`
  // verbatim). #577 line 62: this used to keep only `{id, name}`, so no accepted portrait ever
  // reached Quest Studio.
  return list.filter((m) => m.role !== 'parent').map((m) => ({ id: m.id, name: m.name, avatar: m.avatar ?? null }));
}

// The three rules the picture's own route enforces, so an address is only built for a file it
// would serve: the slug and filename patterns of `tenantSlugParam` / `filenameParam`
// (`server/schemas/common.schemas.js`) and the image extensions `getAvailableIcons` lists
// (`server/services/chore-admin.service.js`). A catalog glyph name ('dishes') has no extension,
// so it can never become a file address.
const SLUG_RE = /^[a-z0-9-]{3,100}$/;
const FILENAME_RE = /^(?!.*\.\.)[a-zA-Z0-9._-]+$/;
const IMAGE_EXT_RE = /\.(png|jpe?g|gif|webp)$/i;

/**
 * A household quest picture → its address on the existing public icons route
 * (`GET /api/files/t/:tenantSlug/icons/:filename`, `server/routes/file.routes.js`), or `null`
 * when the value is not a picture that route would serve (a glyph name, a bad name, no slug).
 */
export function questIconSrc(filename, tenantSlug) {
  if (typeof filename !== 'string' || typeof tenantSlug !== 'string') return null;
  if (!SLUG_RE.test(tenantSlug) || !FILENAME_RE.test(filename) || !IMAGE_EXT_RE.test(filename)) return null;
  return `/api/files/t/${tenantSlug}/icons/${filename}`;
}

/**
 * `toUrl` turns the API PATH into the address the browser loads. The container passes the app's
 * `apiUrl` (`app/src/api/client.js`), the one place that knows the API origin, so this mapping
 * never becomes a second one. Identity by default, which keeps it pure and testable.
 */
const withUrl = (path, toUrl) => (path && typeof toUrl === 'function' ? toUrl(path) : path);

/** Icon picker options → the same options, each with `src` (its picture address, or `null`). */
export function withIconArt(options, tenantSlug, toUrl) {
  const list = Array.isArray(options) ? options.filter(isObject) : [];
  return list.map((o) => ({ ...o, src: withUrl(questIconSrc(o.id, tenantSlug), toUrl) }));
}

/** A `toBoard` board → the same board, each tile with `iconSrc`. `null` stays `null`. */
export function withBoardArt(board, tenantSlug, toUrl) {
  if (!isObject(board) || !Array.isArray(board.columns)) return board ?? null;
  return {
    ...board,
    columns: board.columns.map((col) => (isObject(col) && Array.isArray(col.tiles)
      ? { ...col, tiles: col.tiles.map((t) => (isObject(t) ? { ...t, iconSrc: withUrl(questIconSrc(t.icon, tenantSlug), toUrl) } : t)) }
      : col)),
  };
}

/**
 * `GET /api/admin/chores` → the `board` prop.
 *
 * Seven columns, Monday first, each holding the active quests whose NEXT due date falls on that
 * weekday. `null` when there is no payload, so the page renders nothing rather than a week that
 * belongs to no family.
 *
 * `rangeLabel` names what the board actually shows. The fixture's "Mon Jun 8 – Sun Jun 14" is a
 * specific week, and this is not one: it is every active quest placed on the weekday it next
 * falls on. Saying so is better than a date range that would be wrong for every family.
 */
export function toBoard(choresPayload, kids) {
  if (!isObject(choresPayload) || !Array.isArray(choresPayload.chores)) return null;

  const kidList = Array.isArray(kids) ? kids.filter(isObject) : [];
  const nameById = new Map(kidList.map((k) => [k.id, k.name]));
  const avatarById = new Map(kidList.map((k) => [k.id, k.avatar]));
  const columns = DAY_KEYS.map((key, i) => ({
    id: key,
    day: DAY_LABELS[i],
    full: DAY_FULL[i],
    tiles: [],
  }));
  // Active quests whose due date does not parse cannot sit in a column. They are still on their
  // holder's list, so a reorder that left them out would leave them an old number that can
  // collide with a freshly written one. Reported here so a move can refuse.
  const unplaced = [];

  // #577. These are the field names `getAllChores` really returns
  // (`server/services/chore-admin.service.js`, the `rows.map` near the end). This function used to
  // read the snake_case COLUMN names — `next_estimated_execution_time`,
  // `next_execution_assigned_to_user_id`, `icon_file` — which never reach the browser. Every quest
  // failed the date check below and was skipped, so the live board was seven empty columns.
  // `src/__tests__/unit/services/questBoardContract.test.js` feeds this the service's own output.
  for (const chore of choresPayload.chores) {
    if (!isObject(chore) || chore.active === false) continue;
    const due = new Date(chore.nextExecutionTime);
    const ownerId = Number.isInteger(chore.nextExecutionAssignedTo) ? chore.nextExecutionAssignedTo : null;
    if (Number.isNaN(due.getTime())) {
      unplaced.push({ id: chore.id, ownerId });
      continue;
    }
    columns[isoWeekday(due) - 1].tiles.push({
      id: chore.id,
      title: chore.name,
      icon: chore.iconFile || undefined,
      // `undefined`, not "Unassigned": the tile simply carries no assignee, and the component
      // decides how to show a quest nobody holds.
      assignee: nameById.get(ownerId) || undefined,
      avatar: avatarById.get(ownerId) || undefined,
      // What a move needs, carried off the server's own row. `ownerId` is who holds the quest
      // now, which is whose list `PUT /api/chore-order` reorders.
      ownerId,
      displayOrder: Number.isInteger(chore.displayOrder) ? chore.displayOrder : null,
      scheduleDays: Array.isArray(chore.scheduleDays) ? chore.scheduleDays : null,
      dueAt: due.toISOString(),
      // #577 lane A. The server's own tag, never the name: an edited example stays an example, and
      // a same-name quest the family wrote is never labelled one.
      example: chore.sampleOrigin === 'sample-week-v1',
    });
  }

  return { rangeLabel: 'Every active quest, on the day it next falls', columns: sortBoard(columns, kidList), unplaced };
}

/**
 * Put each day's quests in the order a parent can reason about, and that a move can keep.
 *
 * `display_order` is a PER-PERSON list: `PUT /api/chore-order` refuses a list mixing two people's
 * quests (`server/services/chore.service.js`, "All chores must belong to the same user"), and a
 * rotation appends a quest to the bottom of its new holder's list. Two people's numbers cannot be
 * compared, so ordering a day by the raw number would interleave them at random.
 *
 * So within a day each person's quests sit together — children in the family's own order, then
 * anyone else who holds a quest, then the quests nobody holds — and each group is in its saved
 * order. A tie is broken by quest id, because that is what the child's own screen does
 * (`server/services/dashboard.service.js`, `ORDER BY cc.display_order ASC, cc.chore_id ASC`). The
 * server sends this list by NAME, and a family that never reordered has every number at 0, so
 * keeping the server's order would show the parent a list their child never sees.
 */
export function sortBoard(columns, kids) {
  const kidIndex = new Map((Array.isArray(kids) ? kids : []).map((k, i) => [k && k.id, i]));
  const groupRank = (ownerId) => {
    if (ownerId === null || ownerId === undefined) return [2, 0];
    return kidIndex.has(ownerId) ? [0, kidIndex.get(ownerId)] : [1, ownerId];
  };
  return columns.map((col) => {
    const indexed = col.tiles.map((tile, i) => ({ tile, i }));
    indexed.sort((a, b) => {
      const [ga, ra] = groupRank(a.tile.ownerId);
      const [gb, rb] = groupRank(b.tile.ownerId);
      if (ga !== gb) return ga - gb;
      if (ra !== rb) return ra - rb;
      const oa = Number.isInteger(a.tile.displayOrder) ? a.tile.displayOrder : Infinity;
      const ob = Number.isInteger(b.tile.displayOrder) ? b.tile.displayOrder : Infinity;
      if (oa !== ob) return oa < ob ? -1 : 1;
      if (Number.isInteger(a.tile.id) && Number.isInteger(b.tile.id)) return a.tile.id - b.tile.id;
      return a.i - b.i;
    });
    return { ...col, tiles: indexed.map((x) => x.tile) };
  });
}

/**
 * `GET /api/admin/chores/:id` → the editor's controlled fields, for re-opening a saved quest.
 *
 * **This is the read half of the day-picker defect, and it is the half that stayed invisible.**
 * `b3f3dbd` taught the server to STORE `schedule_days`, `xp` and `requires_approval`. Nothing
 * taught `getChoreById` to return them, and no screen re-opened a saved quest, so nobody noticed.
 * Editing is exactly the screen that exposes it, which is why the server read is repaired in the
 * same change rather than left for the screen to work around.
 *
 * `nextExecutionTime` rides along unchanged. It is not an editor control — `toUpdateChoreBody`
 * needs it to decide whether a schedule change stranded the stored due date on a day the quest
 * may no longer use.
 *
 * @throws when the payload is not a chore. A blank draft would look like a real quest with every
 *   field cleared, and saving it would wipe the quest the parent opened.
 */
export function toEditorDraft(chore) {
  // **Every field the editor can write is required here, not just the id.** The first version
  // checked the id alone, so a resolved `{success: true, chore: {id: 41}}` became a draft with a
  // blank name, reward 0, no assignees and approval ON. The blank name blocks the very first
  // save, and then it does not: a parent types a name, presses save, and those defaults overwrite
  // a real quest. A cross-model reviewer found it. Failing closed is the only version of this
  // function that cannot destroy a quest it could not read.
  const fail = (why) => {
    throw Object.assign(
      new Error(`That quest could not be opened — ${why}`),
      { statusCode: 502 },
    );
  };
  if (!isObject(chore)) fail('the server did not return it');
  if (!Number.isInteger(chore.id) || chore.id <= 0) fail('the server returned no quest id');
  if (typeof chore.name !== 'string' || chore.name.trim() === '') fail('it came back with no name');
  if (typeof chore.periodType !== 'string' || chore.periodType.trim() === '') {
    fail('it came back with no recurrence');
  }
  if (typeof chore.assignmentType !== 'string' || chore.assignmentType.trim() === '') {
    fail('it came back with no assignment rule');
  }
  const reward = typeof chore.reward === 'number' ? chore.reward : Number.parseFloat(chore.reward);
  if (!Number.isFinite(reward) || reward < 0) fail('its reward is missing or not a number');
  if (chore.assignmentConfig !== undefined && !Array.isArray(chore.assignmentConfig)) {
    fail('its assignee list is not a list');
  }
  if (chore.scheduleDays !== null && chore.scheduleDays !== undefined
      && !Array.isArray(chore.scheduleDays)) {
    fail('its days are not a list');
  }
  if (chore.xp !== undefined && chore.xp !== null && !Number.isInteger(chore.xp)) {
    fail('its XP is not a whole number');
  }
  if (chore.requiresApproval !== undefined && chore.requiresApproval !== null
      && typeof chore.requiresApproval !== 'boolean') {
    fail('its approval rule is not true or false');
  }

  const icon = typeof chore.iconFile === 'string' ? chore.iconFile.trim() : '';
  return {
    id: chore.id,
    name: chore.name,
    icon,
    reward,
    // `undefined`, never 0. The column has its own default and this layer must not decide it.
    // `toUpdateChoreBody` never sends it back, because this screen has no XP control — see there.
    xp: Number.isInteger(chore.xp) ? chore.xp : undefined,
    assignedKids: Array.isArray(chore.assignmentConfig)
      ? chore.assignmentConfig.filter((id) => Number.isInteger(id))
      : [],
    // 'random' is the one assignment type that means "do NOT rotate". 'no-assignment' carries no
    // opinion about rotation, so it keeps the rotating default rather than turning the switch off
    // for a quest that simply has nobody on it yet.
    rotate: chore.assignmentType !== 'random',
    // Who holds the quest TODAY. The rotation preview seeds from it, so an open quest's strip
    // carries on from the real holder instead of restarting at the top of the list. `null` when
    // the server names nobody — inventing a starting child would be a guess drawn as a fact.
    currentAssigneeId: Number.isInteger(chore.nextExecutionAssignedTo) ? chore.nextExecutionAssignedTo : null,
    // **Verbatim, NOT normalised.** `createChoreSchema` admits 'hourly' and the screen has no
    // control for it. The first version fell back to 'weekly' here, and `toUpdateChoreBody` then
    // sent that fallback — so renaming an hourly quest turned it weekly. Carrying the real value
    // leaves every frequency button unpressed (`QuestStudio.jsx:1360` compares for equality),
    // which is the honest display, and lets the update body refuse to send what it cannot show.
    frequency: chore.periodType,
    days: fromScheduleDays(chore.scheduleDays),
    requiresApproval: typeof chore.requiresApproval === 'boolean' ? chore.requiresApproval : true,
    nextExecutionTime: chore.nextExecutionTime,
  };
}

/**
 * The quest draft → the `PUT /api/admin/chores/:id` body.
 *
 * **The one real difference from `toCreateChoreBody` is `nextExecutionTime`, and it is deliberate.**
 * Sending it on every edit would push a quest that is due TODAY to tomorrow every time a parent
 * fixed a typo in its name — a silent loss, and the worse of the two defects available here.
 * Omitting it always is the other defect, smaller and self-correcting: a quest due Monday whose
 * days changed to Thursday still comes due that one Monday before the rollover job corrects it.
 *
 * So it is sent in exactly one case: the stored due date falls on a weekday the quest may no
 * longer use. Then the date is walked forward to the soonest allowed day, and only then.
 *
 * `current.nextExecutionTime` comes from `toEditorDraft`, which carries it off the server's own
 * row rather than re-deriving it. Weekday comparison is LOCAL, matching `nextExecutionFrom`.
 */
export function toUpdateChoreBody(draft, kids) {
  const d = isObject(draft) ? draft : {};
  const name = typeof d.name === 'string' ? d.name.trim() : '';
  if (!name) {
    throw Object.assign(new Error('A quest needs a name before it can be saved'), { statusCode: 400 });
  }

  const body = {
    name,
    reward: typeof d.reward === 'number' && d.reward >= 0 ? d.reward : 0,
    ...assignmentFrom(d, kids),
    scheduleDays: toScheduleDays(d.days),
  };

  // The screen HAS a frequency control, but only for daily/weekly/monthly. A quest stored as
  // 'hourly' has no control that can show it, and sending the screen's fallback would turn an
  // hourly quest weekly because a parent renamed it. So the period is sent only when the editor
  // could actually represent what it is showing.
  if (['daily', 'weekly', 'monthly'].includes(d.frequency)) body.periodType = d.frequency;

  // `xp` is NOT sent, and that is the fix rather than an omission.
  //
  // This screen has no XP control. `QuestStudio` DERIVES the XP it displays from the reward
  // (`app/src/pages/QuestStudio.jsx:925-932`, `Math.round(reward * 20)`) and hands that derived
  // number to its host. A quest stored with xp 20 and reward 0.5 therefore offers back 10, and
  // sending it would overwrite the family's stored 20 because a parent fixed a typo.
  //
  // `updateChore` leaves the column alone when the field is absent
  // (`server/services/chore-admin.service.js:511`), which is exactly the rule the create schema
  // already states: undefined means "not sent", and the column decides. Changing XP needs a real
  // XP control, and that is not this change.

  if (typeof d.requiresApproval === 'boolean') body.requiresApproval = d.requiresApproval;
  const icon = typeof d.icon === 'string' ? d.icon.trim() : '';
  if (icon) body.iconFile = icon;

  // `nextExecutionTime` is NOT sent either, and this replaces a version that computed it.
  //
  // That version asked "does the stored due date still fall on an allowed weekday?" using
  // `isoWeekday`, which reads LOCAL time. The family's schedule is in the FAMILY's timezone, not
  // the browser's. A family in UTC with a Monday 00:30Z due time, Monday allowed, is read as
  // Sunday by a browser in America/Edmonton — called stranded, and moved, while still valid.
  // A cross-model reviewer found that, and the browser simply does not hold the fact needed to
  // answer the question.
  //
  // The bound, stated rather than hidden: a quest due Monday whose days a parent moves to
  // Thursday still comes due that one Monday. The rollover job corrects it at the next rollover
  // (`server/jobs/choreRolloverJob.js`), so the error is visible and self-correcting. Moving a
  // quest the family never mis-scheduled is neither.

  return body;
}
